import { useRequestFetch, type UseFetchOptions } from '#app';

import { useFetchKey, type UseFetch } from '@/injectionKeys';
import { buildMicroCMSProxyRequest, createMicroCMSProxyClient, type MicroCMSListDetailParams, type MicroCMSListParams } from '@/wabunka-src/common/core/data/microCMSProxyClient';

import type { MicroCMSListContent, MicroCMSListResponse } from 'microcms-js-sdk';
import type { FetchError } from 'ofetch';

/**
 * setup 内から使う microCMS クライアント。`useRequestFetch()` を渡すため、SSR からプロキシ
 * （自分自身の server route）を呼ぶときにリクエストのヘッダーを引き継ぐ。
 * 素の `$fetch` だと本番以外で Basic 認証に 401 され、取得結果が無言で空になる。
 */
export const useMicroCMSClient = () => {
  return createMicroCMSProxyClient(useRequestFetch());
};

// useFetch の DefaultT を指定するには PickKeys も明示する必要があるが、Nuxt は KeysOf を公開していない。
type KeysOf<T> = (T extends T ? (keyof T extends string ? keyof T : never) : never)[];

/** 未取得・失敗時は null を返す（呼び出し側は null 前提。Nuxt の既定の undefined ではない） */
type MicroCMSFetchOptions<T> = UseFetchOptions<T, T, KeysOf<T>, null>;

const useMicroCMSGet = <T>(params: MicroCMSListParams & { contentId?: string }, fetchOptions: MicroCMSFetchOptions<T> = {}) => {
  // 同ディレクトリの useAsyncFetchFromPublicApi 等と同じ DI 規約に乗せる。
  // テストでは useFetchForTest が注入され、MSW でネットワークを差し替えられる。
  const useFetch = inject(useFetchKey) as UseFetch;
  const { path, query } = buildMicroCMSProxyRequest(params);

  return useFetch<T, FetchError, string, 'get', T, T, KeysOf<T>, null>(path, {
    default: () => null,
    ...fetchOptions,
    query,
    method: 'get',
  });
};

export const useMicroCMSGetList = <T>(params: MicroCMSListParams, fetchOptions: MicroCMSFetchOptions<MicroCMSListResponse<T>> = {}) => {
  return useMicroCMSGet<MicroCMSListResponse<T>>(params, fetchOptions);
};

export const useMicroCMSGetListDetail = <T>(params: MicroCMSListDetailParams, fetchOptions: MicroCMSFetchOptions<T & MicroCMSListContent> = {}) => {
  return useMicroCMSGet<T & MicroCMSListContent>(params, fetchOptions);
};
